import { useEffect, useRef, useState, type FC, type CSSProperties } from "react";
import { Text, Button } from "@fluentui/react-components";
import { Stack, Image } from "@fluentui/react";
import { useMsal } from '@azure/msal-react';
import { t } from "@/strings/index";
import { initializeEcsClient, getFeatureFlag } from '../services/ecs';
import { logDev as debugLog, errorDev } from '@/logging/devConsole';
import ErrorScreen from './common/ErrorScreen';
import {
  logScreenRender,
  logButtonClick,
  collectEntrySource,
  startPageRenderTiming,
  endPageRenderTiming,
} from "../telemetry/telemetryEvents";
import microsoftLogo from "@/assets/microsoftLogo.png";
import teamsLogo from "@/assets/teams.svg";
import "../styles/WelcomeScreen.css";

interface WelcomeScreenProps {
  onGetStarted: () => void;
}

const LIVE_REGION_DELAY_MS = 150;
const visuallyHiddenStyle: CSSProperties = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  border: 0,
  padding: 0,
  clip: "rect(0 0 0 0)",
  overflow: "hidden",
};

export const WelcomeScreen: FC<WelcomeScreenProps> = ({ onGetStarted }) => {
  const { accounts, inProgress, instance } = useMsal();
  const getStartedButtonRef = useRef<HTMLButtonElement | null>(null);
  const liveRegionTimerRef = useRef<number | undefined>(undefined);
  const [liveMessage, setLiveMessage] = useState<string>("");
  const [ecsReady, setEcsReady] = useState(false);
  const [showKillSwitch, setShowKillSwitch] = useState(false);

  const titleText = t("welcome.title");
  const descriptionText = t("welcome.description");
  const buttonText = t("welcome.getStarted");

  // Initialize ECS client and wait before showing content
  useEffect(() => {
    const initializeEcs = async () => {
      if (inProgress !== "none") {
        debugLog('WelcomeScreen: MSAL still in progress, waiting...');
        return;
      }

      try {
        debugLog('WelcomeScreen: Initializing ECS client...');
        await initializeEcsClient();
        debugLog('WelcomeScreen: ECS client initialized successfully');
        
        // Check kill switch after ECS initializes
        const killSwitch = getFeatureFlag('killSwitch', false);
        if (killSwitch) {
          debugLog('WelcomeScreen: Kill switch is enabled');
          setShowKillSwitch(true);
        }
      } catch (error) {
        errorDev('WelcomeScreen: ECS initialization failed:', error);
        // Continue with defaults (kill switch will be false)
      } finally {
        setEcsReady(true);
      }
    };

    initializeEcs();
  }, [inProgress]);

  useEffect(() => {
    if (!ecsReady) {
      return;
    }

    // Clear any previous session to ensure fresh start for new journey
    // This removes scenarioCompleted, authRedirectInProgress, and any other wizard state
    sessionStorage.clear();
    debugLog('[WelcomeScreen] Cleared previous session for fresh start');

    // Capture entry source from URL before it's lost (e.g. ?source=admin-center)
    let entrySource: string | null = null;
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      entrySource = urlParams.get('source');
    }

    // Start page render timing
    startPageRenderTiming('welcome');

    // Log screen view (starts main scenario, logs UserBI, marks 'welcome' step)
    logScreenRender("WelcomeScreen");

    // Collect entry source into telemetry after scenario has started
    // Validate: max 100 chars, lowercase alphanumeric + hyphens only
    if (entrySource && entrySource.length <= 100 && /^[a-z0-9-]+$/.test(entrySource)) {
      collectEntrySource(entrySource);
      debugLog('[WelcomeScreen] Entry source captured:', entrySource);
    }

    endPageRenderTiming('welcome');
    getStartedButtonRef.current?.focus();

    if (typeof window !== "undefined") {
      liveRegionTimerRef.current = window.setTimeout(() => {
        setLiveMessage(`${titleText}. ${descriptionText}`);
      }, LIVE_REGION_DELAY_MS);
    }

    return () => {
      if (typeof window !== "undefined" && liveRegionTimerRef.current !== undefined) {
        window.clearTimeout(liveRegionTimerRef.current);
      }
    };
  }, [ecsReady, titleText, descriptionText]);

  const handleGetStarted = () => {
    // Log button click
    logButtonClick("WelcomeScreen", "GetStarted");
    onGetStarted();
  };

  // Wait for ECS to initialize before showing anything
  if (!ecsReady) {
    return null;
  }

  // Show error screen if kill switch is enabled
  if (showKillSwitch) {
    return <ErrorScreen />;
  }

  return (
      <Stack as="main" horizontal verticalAlign="center" className="welcome-screen">
        <Stack as="section" className="welcome-panel">
          <span aria-live="polite" role="status" style={visuallyHiddenStyle}>
            {liveMessage}
          </span>
          <Image src={teamsLogo} alt="Microsoft Teams" className="welcome-logo" />
          <Text as="h1" className="welcome-title">{titleText}</Text>
          <Text as="p" className="welcome-desc">{descriptionText}</Text>
          <Button
            className="welcome-button"
            appearance="primary"
            onClick={handleGetStarted}
            ref={getStartedButtonRef}
          >
            {buttonText}
          </Button>
        </Stack>
        <Image
          src={microsoftLogo}
          alt=""
          aria-hidden
          role="presentation"
          className="welcome-microsoft-logo"
        />
      </Stack>
  );
};

export default WelcomeScreen;